import React from 'react';
import classNames from 'classnames';

import type { MediaRecorderState } from 'js/src/components/app/recorder/types';
import { DURATION_FORMAT, toColonSeparatedDuration } from 'js/src/libs/readable-duration';

const TICK_INTERVAL = 1000; // 1s
const DEFAULT_MAX_DURATION_MS = 1000 * 60 * 10; // 10 minutes

type RecordingClockProps = {
    mediaRecorderState: MediaRecorderState;
    getRecordedDuration: () => number;
    onRecordedDurationChange: (recordedTime: number) => void;
    /**
     * Denominator shown next to the elapsed time (e.g. "00:30" for a 30-second
     * recording cap).
     */
    maxDurationMs?: number;
};

const RecorderClock: React.FC<RecordingClockProps> = React.memo(function RecordingClock({
    mediaRecorderState,
    getRecordedDuration,
    onRecordedDurationChange,
    maxDurationMs = DEFAULT_MAX_DURATION_MS,
}) {
    const intervalTickerRef = React.useRef<number | null>(null);

    const [recordedTimeInMs, setRecordedTimeInMs] = React.useState(getRecordedDuration());

    React.useEffect(() => {
        // each clock should only hold 1 interval ticker
        if (!intervalTickerRef.current) {
            intervalTickerRef.current = window.setInterval(() => {
                const duration = getRecordedDuration();
                setRecordedTimeInMs(duration);
                onRecordedDurationChange(duration);
            }, TICK_INTERVAL);
        }
    }, [getRecordedDuration, onRecordedDurationChange]);

    React.useEffect(() => {
        return () => {
            if (intervalTickerRef.current === null) return;
            window.clearInterval(intervalTickerRef.current);
            intervalTickerRef.current = null;
        };
    }, []);

    return (
        <div className={'recording-clock d-flex align-items-center'}>
            <div
                className={classNames(
                    'material-symbols-outlined icon-l5 icon-l5--small material-symbols-f1',
                    {
                        'color-error-300': mediaRecorderState === 'recording',
                    },
                )}
            >
                {mediaRecorderState === 'recording' ? 'circle' : 'pause'}
            </div>

            <div className="font-l5 font-l5--h4 ms-l5-8 tabular-nums">
                {toColonSeparatedDuration(recordedTimeInMs, DURATION_FORMAT.MINUTES_SECONDS)}
            </div>

            <div className="color-grey-600 tabular-nums ms-l5-4">
                &nbsp;/&nbsp;
                {toColonSeparatedDuration(maxDurationMs, DURATION_FORMAT.MINUTES_SECONDS)}
            </div>
        </div>
    );
});

export default RecorderClock;
